import { ActionContainer } from "@/components/layout/ActionContainer";
import { openBokaBookingModal } from "@/features/bookingModal/openBokaBookingModal";
import { useModals } from "@/features/modals/hooks";
import { STAGE_ACTIONS_SECTION_IDS } from "@/features/project/projectTopSection/stages/config";
import { SectionItem } from "@/features/tasks/SectionItem";
import type { Opportunity } from "@repo/api";
import {
  Button,
  calendarIcon,
  clipboardPencilIcon,
  speechBubbleIcon,
} from "@repo/skapa";
import { t } from "@repo/translation";
import { useSettings } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";
import { useState } from "react";

interface ActivityActionsProps {
  opportunity: Opportunity;
}

export const ActivityActions = ({ opportunity }: ActivityActionsProps) => {
  const { pushModal } = useModals();
  const { boka_config } = useSettings();

  const isBokaAppointmentIntegrationEnabled =
    boka_config?.enable_boka_appointment_modal_integration ?? false;
  const [isBokaLoading, setIsBokaLoading] = useState(false);

  const handleTaskAction = () => {
    pushModal?.({
      type: "TASK",
      initialData: {
        selectedCoworkers: opportunity.owner ? [opportunity.owner] : undefined,
      },
      opportunityId: opportunity.id,
      context: "PROJECT",
    });
    trackAnalytics("task");
  };

  const handleSpokeWithCustomer = () => {
    pushModal?.({
      type: "SPOKE_WITH_CUSTOMER",
    });
    trackAnalytics("spoke_with_customer");
  };

  const handleAppointmentAction = () => {
    pushModal?.({
      type: "BOOK_APPOINTMENT",
    });
    trackAnalytics("book_appointment");
  };

  const handleBookBokaAppointment = async () => {
    trackAnalytics("book_boka_appointment");

    setIsBokaLoading(true);
    try {
      await openBokaBookingModal({
        // TODO: hardcoded for Boka BE integration testing — derive from opportunity/store config
        postalCode: "4450-745",
        serviceProductId: "BUSINESS_PLANNING",
        bokaAppointmentId: String(opportunity.id),
        coworkerEmail: opportunity.owner ?? "",
        locationType: "STORE",
        from: new Date().toISOString(),
        ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    } finally {
      setIsBokaLoading(false);
    }
  };

  return (
    <SectionItem
      title={t("label.record_an_activity")}
      id={STAGE_ACTIONS_SECTION_IDS.RECORD_ACTIVITY_SECTION}
    >
      <ActionContainer>
        <Button
          size="small"
          text={t("label.task")}
          ssrIcon={clipboardPencilIcon}
          onClick={handleTaskAction}
        />
        <Button
          size="small"
          text={t("label.appointment")}
          ssrIcon={calendarIcon}
          onClick={handleAppointmentAction}
        />
        {isBokaAppointmentIntegrationEnabled && (
          <Button
            size="small"
            text={t("action.book_appointment")}
            ssrIcon={calendarIcon}
            loading={isBokaLoading}
            disabled={isBokaLoading}
            onClick={() => {
              void handleBookBokaAppointment();
            }}
          />
        )}
        <Button
          size="small"
          text={t("action.spoke_with_customer")}
          ssrIcon={speechBubbleIcon}
          onClick={handleSpokeWithCustomer}
        />
      </ActionContainer>
    </SectionItem>
  );
};
